// Voice & AI Search Tracking Dashboard
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Mic, Brain, Target, TrendingUp, CheckCircle, AlertCircle, RefreshCw, Sparkles } from 'lucide-react';

export function VoiceAISearch() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [featuredSnippets, setFeaturedSnippets] = useState([]);
  const [aiMentions, setAIMentions] = useState([]);
  const [questionKeywords, setQuestionKeywords] = useState([]);
  const [voiceScores, setVoiceScores] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      setLoading(true);
      const data = await api.request('/voice-ai-search/dashboard');
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadFeaturedSnippets() {
    try {
      const data = await api.request('/voice-ai-search/featured-snippets');
      setFeaturedSnippets(data.snippets || []);
    } catch (err) {
      console.error('Failed to load snippets:', err);
    }
  }

  async function loadAIMentions() {
    try {
      const data = await api.request('/voice-ai-search/ai-mentions');
      setAIMentions(data.mentions || []);
    } catch (err) {
      console.error('Failed to load AI mentions:', err);
    }
  }

  async function loadQuestionKeywords() {
    try {
      const data = await api.request('/voice-ai-search/question-keywords');
      setQuestionKeywords(data.question_keywords || []);
    } catch (err) {
      console.error('Failed to load question keywords:', err);
    }
  }

  async function handleCheckSnippets() {
    if (!confirm('Check featured snippets for all keywords? This may take 1-2 minutes.')) return;
    setChecking(true);
    try {
      await api.request('/voice-ai-search/check-snippets', { method: 'POST' });
      alert('✅ Featured snippets checked!');
      await loadDashboard();
      await loadFeaturedSnippets();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setChecking(false);
    }
  }

  async function handleTestAI() {
    if (!confirm('Test AI platforms with sample queries? This will use AI credits and take 1-2 minutes.')) return;
    setChecking(true);
    try {
      const result = await api.request('/voice-ai-search/test-ai-mentions', { method: 'POST' });
      alert(`✅ Tested ${result.results.length} AI queries! Mention rate: ${result.mention_rate}%`);
      await loadDashboard();
      await loadAIMentions();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'snippets' && featuredSnippets.length === 0) {
      loadFeaturedSnippets();
    } else if (activeTab === 'ai-mentions' && aiMentions.length === 0) {
      loadAIMentions();
    } else if (activeTab === 'questions' && questionKeywords.length === 0) {
      loadQuestionKeywords();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <Target className="w-4 h-4 mr-2" />
              Featured Snippets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats.featured_snippets?.owned || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Owned / {stats.featured_snippets?.opportunities || 0} opportunities
            </div>
            {stats.featured_snippets?.ownership_rate > 0 && (
              <div className="text-xs text-green-600 mt-1">
                {stats.featured_snippets.ownership_rate}% ownership rate
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <Brain className="w-4 h-4 mr-2" />
              AI Mentions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {stats.ai_mentions?.mention_rate || 0}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.ai_mentions?.mentioned || 0} of {stats.ai_mentions?.total_queries || 0} queries
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <Mic className="w-4 h-4 mr-2" />
              Question Keywords
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {stats.question_keywords?.total || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Voice-friendly keywords tracked
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <Sparkles className="w-4 h-4 mr-2" />
              Voice Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {stats.voice_optimization?.average_score || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Average optimization score
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <Button
          onClick={handleCheckSnippets}
          disabled={checking}
          variant="outline"
        >
          {checking ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Check Featured Snippets
        </Button>
        <Button
          onClick={handleTestAI}
          disabled={checking}
          className="bg-purple-600"
        >
          {checking ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Brain className="w-4 h-4 mr-2" />
          )}
          Test AI Platforms
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'overview'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('snippets')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'snippets'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Featured Snippets
        </button>
        <button
          onClick={() => setActiveTab('ai-mentions')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'ai-mentions'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          AI Mentions
        </button>
        <button
          onClick={() => setActiveTab('questions')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeTab === 'questions'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Question Keywords
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Recent AI Mentions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Brain className="w-5 h-5 mr-2 text-purple-600" />
                Recent AI Search Mentions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.recent_mentions?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.recent_mentions.map((mention) => (
                    <div key={mention.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{mention.query}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Platform: {mention.ai_platform} • {new Date(mention.checked_at).toLocaleDateString()}
                          </div>
                          {mention.response_text && (
                            <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                              {mention.response_text.substring(0, 200)}...
                            </div>
                          )}
                        </div>
                        {mention.mentioned ? (
                          <CheckCircle className="w-5 h-5 text-green-600 ml-2" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-gray-400 ml-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">
                  No AI mentions tested yet. Click "Test AI Platforms" above.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Top Question Keywords */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mic className="w-5 h-5 mr-2 text-blue-600" />
                Top Question Keywords for Voice Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.top_questions?.length > 0 ? (
                <div className="space-y-2">
                  {dashboardData.top_questions.map((kw) => (
                    <div key={kw.id} className="flex items-center justify-between border-b pb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{kw.keyword}</div>
                        {kw.search_volume && (
                          <div className="text-xs text-gray-500">
                            {kw.search_volume.toLocaleString()} searches/mo
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {kw.google_rank && (
                          <Badge className="bg-blue-600 text-white">
                            #{kw.google_rank}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">
                  No question keywords tracked yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Featured Snippets Tab */}
      {activeTab === 'snippets' && (
        <Card>
          <CardHeader>
            <CardTitle>Featured Snippet Tracking</CardTitle>
          </CardHeader>
          <CardContent>
            {featuredSnippets.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Keyword</th>
                      <th className="text-center py-3 px-2">Has Snippet</th>
                      <th className="text-center py-3 px-2">Type</th>
                      <th className="text-center py-3 px-2">Owned</th>
                      <th className="text-left py-3 px-2">Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featuredSnippets.map((snippet) => (
                      <tr key={snippet.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2 font-medium">{snippet.keyword}</td>
                        <td className="text-center py-3 px-2">
                          {snippet.has_snippet ? (
                            <CheckCircle className="w-4 h-4 text-green-600 inline" />
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-2">
                          {snippet.snippet_type ? (
                            <Badge variant="outline">{snippet.snippet_type}</Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-2">
                          {snippet.owned_by_upost ? (
                            <Badge className="bg-green-600 text-white">✓ UPost</Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-xs text-gray-600">
                          {snippet.current_owner || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                No snippets checked yet. Click "Check Featured Snippets" above.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Mentions Tab */}
      {activeTab === 'ai-mentions' && (
        <Card>
          <CardHeader>
            <CardTitle>AI Search Mention History</CardTitle>
          </CardHeader>
          <CardContent>
            {aiMentions.length > 0 ? (
              <div className="space-y-3">
                {aiMentions.map((mention) => (
                  <div key={mention.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium">{mention.query}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {mention.ai_platform} • {new Date(mention.checked_at).toLocaleDateString()}
                        </div>
                      </div>
                      {mention.mentioned ? (
                        <Badge className="bg-green-100 text-green-800">
                          ✓ Mentioned
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not Mentioned</Badge>
                      )}
                    </div>
                    {mention.response_text && (
                      <div className="text-sm text-gray-700 mt-2 p-3 bg-gray-50 rounded">
                        {mention.response_text}
                      </div>
                    )}
                    {mention.position && (
                      <div className="text-xs text-green-600 mt-2">
                        Cited as source #{mention.position}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                No AI mentions tested yet. Click "Test AI Platforms" above.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Question Keywords Tab */}
      {activeTab === 'questions' && (
        <Card>
          <CardHeader>
            <CardTitle>Voice-Friendly Question Keywords</CardTitle>
          </CardHeader>
          <CardContent>
            {questionKeywords.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Question</th>
                      <th className="text-center py-3 px-2">Type</th>
                      <th className="text-center py-3 px-2">Volume</th>
                      <th className="text-center py-3 px-2">Rank</th>
                      <th className="text-center py-3 px-2">Snippet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionKeywords.map((kw) => (
                      <tr key={kw.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2 font-medium">{kw.keyword}</td>
                        <td className="text-center py-3 px-2">
                          <Badge variant="outline" className="uppercase">
                            {kw.question_type}
                          </Badge>
                        </td>
                        <td className="text-center py-3 px-2">
                          {kw.search_volume ? kw.search_volume.toLocaleString() : '—'}
                        </td>
                        <td className="text-center py-3 px-2">
                          {kw.google_rank ? (
                            <Badge className="bg-blue-600 text-white">#{kw.google_rank}</Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-2">
                          {kw.snippet_owned ? (
                            <CheckCircle className="w-4 h-4 text-green-600 inline" />
                          ) : kw.has_featured_snippet ? (
                            <AlertCircle className="w-4 h-4 text-yellow-600 inline" />
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                Loading question keywords...
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold mb-2 flex items-center">
            <Sparkles className="w-4 h-4 mr-2" />
            About Voice & AI Search Tracking
          </h4>
          <div className="text-sm text-gray-700 space-y-2">
            <p>
              <strong>Featured Snippets:</strong> Position 0 results that voice assistants often read aloud. Owning snippets dramatically increases voice search visibility.
            </p>
            <p>
              <strong>AI Mentions:</strong> Tracks if UPost appears in ChatGPT, Perplexity, and other AI search results. Higher mention rate = better AI visibility.
            </p>
            <p>
              <strong>Question Keywords:</strong> Conversational queries starting with how/what/why/etc. These keywords get 10x more voice searches than regular keywords.
            </p>
            <p className="text-xs text-purple-700 mt-3">
              💡 Tip: Focus on owning featured snippets for high-volume question keywords to dominate voice search results!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
